import type { Cell, LifeSnapshot } from "../engine/engine";

export interface GridSize {
  readonly width: number;
  readonly height: number;
}

export interface FittedGrid extends GridSize {
  readonly cellSize: number;
}

export interface LifeViewport extends GridSize {
  readonly x: number;
  readonly y: number;
}

export interface ViewPoint {
  readonly x: number;
  readonly y: number;
}

export interface LifeViewPalette {
  readonly background: string;
  readonly grid: string;
  readonly alive: string;
  readonly hover: string;
}

export function fitGridToRows(
  width: number,
  height: number,
  rows: number,
): FittedGrid {
  if (!Number.isInteger(rows) || rows < 1) {
    throw new RangeError("rows must be a positive integer");
  }

  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const cellSize = safeHeight / rows;

  return {
    width: Math.max(1, Math.ceil(safeWidth / cellSize)),
    height: rows,
    cellSize,
  };
}

export function positionViewport(
  size: GridSize,
  focus: ViewPoint,
  horizontalFocus = 0.5,
): LifeViewport {
  if (
    !Number.isSafeInteger(size.width) ||
    size.width <= 0 ||
    !Number.isSafeInteger(size.height) ||
    size.height <= 0
  ) {
    throw new RangeError("Viewport dimensions must be positive integers.");
  }
  if (!Number.isFinite(focus.x) || !Number.isFinite(focus.y)) {
    throw new RangeError("Camera focus must contain finite coordinates.");
  }
  if (
    !Number.isFinite(horizontalFocus) ||
    horizontalFocus < 0 ||
    horizontalFocus > 1
  ) {
    throw new RangeError("horizontalFocus must be between 0 and 1.");
  }

  return {
    x: Math.floor(focus.x - size.width * horizontalFocus),
    y: Math.floor(focus.y - size.height / 2),
    width: size.width,
    height: size.height,
  };
}

export class CanvasLifeRenderer {
  private readonly context: CanvasRenderingContext2D;
  private readonly palette: LifeViewPalette;
  private cssWidth = 1;
  private cssHeight = 1;
  private columns = 1;
  private rows = 1;
  private resolvedCellSize = 1;
  private focusX = 0;
  private focusY = 0;
  private horizontalFocus = 0.5;
  private viewportX = 0;
  private viewportY = 0;
  private hoveredCell: Cell | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    palette: LifeViewPalette,
    private readonly fittedRows = 72,
  ) {
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) {
      throw new Error("Canvas 2D rendering is unavailable");
    }
    this.context = context;
    this.palette = palette;
  }

  get cellSize(): number {
    return this.resolvedCellSize;
  }

  get viewport(): LifeViewport {
    return {
      x: this.viewportX,
      y: this.viewportY,
      width: this.columns,
      height: this.rows,
    };
  }

  setCamera(focus: ViewPoint, horizontalFocus = 0.5): void {
    positionViewport({ width: 1, height: 1 }, focus, horizontalFocus);
    this.focusX = focus.x;
    this.focusY = focus.y;
    this.horizontalFocus = horizontalFocus;
    this.updateViewportOrigin();
  }

  resize(): GridSize {
    const bounds = this.canvas.getBoundingClientRect();
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.cssWidth = Math.max(1, bounds.width);
    this.cssHeight = Math.max(1, bounds.height);
    this.canvas.width = Math.round(this.cssWidth * pixelRatio);
    this.canvas.height = Math.round(this.cssHeight * pixelRatio);
    this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    const grid = fitGridToRows(
      this.cssWidth,
      this.cssHeight,
      this.fittedRows,
    );
    this.columns = grid.width;
    this.rows = grid.height;
    this.resolvedCellSize = grid.cellSize;
    this.updateViewportOrigin();

    return {
      width: this.columns,
      height: this.rows,
    };
  }

  render(snapshot: LifeSnapshot): void {
    this.context.clearRect(0, 0, this.cssWidth, this.cssHeight);
    this.context.fillStyle = this.palette.background;
    this.context.fillRect(0, 0, this.cssWidth, this.cssHeight);
    this.drawGrid(this.columns, this.rows);

    this.context.fillStyle = this.palette.alive;
    const inset = Math.max(1, Math.round(this.cellSize * 0.12));
    const size = this.cellSize - inset * 2;
    for (let screenY = 0; screenY < this.rows; screenY += 1) {
      const worldY = this.viewportY + screenY;
      const snapshotY = worldY - snapshot.y;
      if (snapshotY < 0 || snapshotY >= snapshot.height) {
        continue;
      }
      for (let screenX = 0; screenX < this.columns; screenX += 1) {
        const worldX = this.viewportX + screenX;
        const snapshotX = worldX - snapshot.x;
        if (
          snapshotX >= 0 &&
          snapshotX < snapshot.width &&
          snapshot.cells[snapshotY * snapshot.width + snapshotX] === 1
        ) {
          this.context.fillRect(
            screenX * this.cellSize + inset,
            screenY * this.cellSize + inset,
            size,
            size,
          );
        }
      }
    }

    if (this.hoveredCell && this.isVisible(this.hoveredCell)) {
      const screenX = this.hoveredCell.x - this.viewportX;
      const screenY = this.hoveredCell.y - this.viewportY;
      this.context.fillStyle = this.palette.hover;
      this.context.fillRect(
        screenX * this.cellSize + inset,
        screenY * this.cellSize + inset,
        size,
        size,
      );
    }
  }

  cellAt(clientX: number, clientY: number): Cell | null {
    const bounds = this.canvas.getBoundingClientRect();
    const x = Math.floor((clientX - bounds.left) / this.cellSize);
    const y = Math.floor((clientY - bounds.top) / this.cellSize);
    if (
      x < 0 ||
      y < 0 ||
      x >= this.columns ||
      y >= this.rows
    ) {
      return null;
    }
    return { x: x + this.viewportX, y: y + this.viewportY };
  }

  setHoveredCell(cell: Cell | null): void {
    this.hoveredCell = cell;
  }

  private updateViewportOrigin(): void {
    const viewport = positionViewport(
      { width: this.columns, height: this.rows },
      { x: this.focusX, y: this.focusY },
      this.horizontalFocus,
    );
    this.viewportX = viewport.x;
    this.viewportY = viewport.y;
  }

  private isVisible(cell: Cell): boolean {
    return (
      cell.x >= this.viewportX &&
      cell.x < this.viewportX + this.columns &&
      cell.y >= this.viewportY &&
      cell.y < this.viewportY + this.rows
    );
  }

  private drawGrid(columns: number, rows: number): void {
    this.context.beginPath();
    this.context.strokeStyle = this.palette.grid;
    this.context.lineWidth = 1;

    for (let x = 0; x <= columns; x += 1) {
      const coordinate = x * this.cellSize + 0.5;
      this.context.moveTo(coordinate, 0);
      this.context.lineTo(coordinate, this.cssHeight);
    }
    for (let y = 0; y <= rows; y += 1) {
      const coordinate = y * this.cellSize + 0.5;
      this.context.moveTo(0, coordinate);
      this.context.lineTo(this.cssWidth, coordinate);
    }
    this.context.stroke();
  }
}
